import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import axios from '../api/axios';
import useRefreshToken from '../hooks/useRefreshToken';

const LOGIN_URL = '/Login/Login';

const Login = () => {
    const { auth, setAuth, persist, setPersist } = useAuth();
    const refresh = useRefreshToken();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || '/Dashboard';

    const userRef = useRef();
    const errRef = useRef();

    const [user, setUser]       = useState('');
    const [pwd, setPwd]         = useState('');
    const [errMsg, setErrMsg]   = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let mounted = true;
        const timeout = (ms) => new Promise((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), ms)
        );
        const verifyRefreshToken = async () => {
            try {
                await Promise.race([refresh(), timeout(4000)]);
            } catch (_) {}
            finally { mounted && setIsLoading(false); }
        };
        !auth?.accessToken && persist ? verifyRefreshToken() : setIsLoading(false);
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (auth?.accessToken) navigate(from, { replace: true });
    }, []);

    useEffect(() => { setErrMsg(''); }, [user, pwd]);

    useEffect(() => { localStorage.setItem('persist', persist); }, [persist]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await axios.post(
                LOGIN_URL,
                JSON.stringify({ username: user, password: pwd }),
                { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
            );
            setAuth({ user, role: response?.data?.role, accessToken: response?.data?.accessToken });
            setUser('');
            setPwd('');
            navigate(from, { replace: true });
        } catch (err) {
            if (!err?.response)             setErrMsg('No server response. Check your connection.');
            else if (err.response?.status === 400) setErrMsg('Username and password are required.');
            else if (err.response?.status === 401) setErrMsg('Invalid username or password.');
            else                                   setErrMsg('Sign in failed. Please try again.');
            errRef.current?.focus();
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoading) return (
        <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
                <div className="spinner-border text-light mb-3" role="status" />
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Loading...</div>
            </div>
        </div>
    );

    return (
        <div style={styles.page}>
            {/* Left panel */}
            <div style={styles.left}>
                <div style={styles.leftInner}>
                    <div style={styles.brandRow}>
                        <img src="/assets/img/eda.png" alt="logo" style={styles.logo} />
                        <span style={styles.brandName}>M&amp;A Analytics</span>
                    </div>

                    <h1 style={styles.leftTitle}>
                        Electronic Design Automation
                        <br />
                        M&amp;A Research Platform
                    </h1>
                    <p style={styles.leftSub}>
                        A comprehensive online tool for studying the dynamics and evaluating
                        the viability of EDA companies in times of technological disruption.
                    </p>

                    <div style={styles.statsGrid}>
                        {[
                            { value: '661', label: 'M&A Transactions' },
                            { value: '1975–2025', label: 'Date Range' },
                            { value: '5',   label: 'Cluster Archetypes' },
                            { value: '30',  label: 'M&A Waves Detected' },
                            { value: '8',   label: 'Publications' },
                            { value: '0.879', label: 'Silhouette Score' },
                        ].map(s => (
                            <div key={s.label} style={styles.statBox}>
                                <div style={styles.statValue}>{s.value}</div>
                                <div style={styles.statLabel}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={styles.leftFooter}>
                        Technical University of Sofia &nbsp;·&nbsp; PhD Research
                    </div>
                </div>
            </div>

            {/* Right panel */}
            <div style={styles.right}>
                <div style={styles.formCard}>
                    <div style={styles.formHeader}>
                        <div style={styles.formIcon}>
                            <i className="bi bi-person-lock" style={{ fontSize: '1.4rem', color: '#3b5bdb' }} />
                        </div>
                        <h2 style={styles.formTitle}>Sign In</h2>
                        <p style={styles.formSub}>Access the research platform</p>
                    </div>

                    {errMsg && (
                        <div ref={errRef} tabIndex={-1} style={styles.errorBox} aria-live="assertive">
                            <i className="bi bi-exclamation-circle-fill me-2" />
                            {errMsg}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={styles.form}>
                        <div style={styles.field}>
                            <label style={styles.label} htmlFor="username">Username</label>
                            <div style={styles.inputWrap}>
                                <i className="bi bi-person" style={styles.inputIcon} />
                                <input
                                    id="username"
                                    type="text"
                                    ref={userRef}
                                    autoComplete="off"
                                    value={user}
                                    onChange={e => setUser(e.target.value)}
                                    required
                                    style={styles.input}
                                    placeholder="Enter your username"
                                />
                            </div>
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label} htmlFor="password">Password</label>
                            <div style={styles.inputWrap}>
                                <i className="bi bi-lock" style={styles.inputIcon} />
                                <input
                                    id="password"
                                    type="password"
                                    value={pwd}
                                    onChange={e => setPwd(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    style={styles.input}
                                    placeholder="Enter your password"
                                />
                            </div>
                        </div>

                        <label style={styles.persistRow}>
                            <input
                                type="checkbox"
                                checked={persist}
                                onChange={() => setPersist(p => !p)}
                                style={{ marginRight: 8, accentColor: '#3b5bdb' }}
                            />
                            <span style={{ fontSize: '0.85rem', color: '#555' }}>Keep me signed in</span>
                        </label>

                        <button type="submit" disabled={submitting} style={{
                            ...styles.btn,
                            opacity: submitting ? 0.7 : 1,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                        }}>
                            {submitting
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" /> Signing in...</>
                                : 'Sign In'}
                        </button>
                    </form>

                    <p style={styles.version}>EDA-DYV Platform &nbsp;·&nbsp; v2.0</p>
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: {
        display: 'flex',
        minHeight: '100vh',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    left: {
        flex: '0 0 55%',
        background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 60%, #1e3a5f 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 56px',
    },
    leftInner: {
        maxWidth: 520,
        width: '100%',
    },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 36,
    },
    logo: {
        height: 36,
        filter: 'brightness(0) invert(1)',
    },
    brandName: {
        fontSize: '1.15rem',
        fontWeight: 700,
        color: '#fff',
        letterSpacing: '-0.2px',
    },
    leftTitle: {
        fontSize: '2rem',
        fontWeight: 800,
        color: '#fff',
        lineHeight: 1.25,
        margin: '0 0 16px',
    },
    leftSub: {
        fontSize: '0.92rem',
        color: 'rgba(255,255,255,0.62)',
        lineHeight: 1.7,
        margin: '0 0 36px',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 14,
        marginBottom: 40,
    },
    statBox: {
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: '14px 10px',
        textAlign: 'center',
    },
    statValue: {
        fontSize: '1.25rem',
        fontWeight: 800,
        color: '#7ea8f8',
        lineHeight: 1.2,
        marginBottom: 4,
    },
    statLabel: {
        fontSize: '0.7rem',
        color: 'rgba(255,255,255,0.5)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    leftFooter: {
        fontSize: '0.78rem',
        color: 'rgba(255,255,255,0.35)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        paddingTop: 20,
    },
    right: {
        flex: '0 0 45%',
        background: '#f4f6f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
    },
    formCard: {
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        padding: '40px 36px',
        width: '100%',
        maxWidth: 400,
    },
    formHeader: {
        textAlign: 'center',
        marginBottom: 28,
    },
    formIcon: {
        width: 56,
        height: 56,
        background: '#eef1fd',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 14px',
    },
    formTitle: {
        fontSize: '1.5rem',
        fontWeight: 800,
        color: '#1a1a1a',
        margin: '0 0 6px',
    },
    formSub: {
        fontSize: '0.85rem',
        color: '#888',
        margin: 0,
    },
    errorBox: {
        background: '#fff5f5',
        border: '1px solid #fca5a5',
        borderRadius: 8,
        padding: '10px 14px',
        color: '#dc2626',
        fontSize: '0.85rem',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        outline: 'none',
    },
    form: { display: 'flex', flexDirection: 'column', gap: 18 },
    field: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: {
        fontSize: '0.82rem',
        fontWeight: 700,
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    inputWrap: { position: 'relative' },
    inputIcon: {
        position: 'absolute',
        left: 13,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
        fontSize: '0.95rem',
        pointerEvents: 'none',
    },
    input: {
        width: '100%',
        padding: '11px 14px 11px 38px',
        border: '1.5px solid #e5e7eb',
        borderRadius: 8,
        fontSize: '0.92rem',
        color: '#1a1a1a',
        background: '#fafafa',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s',
    },
    persistRow: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        marginTop: -4,
    },
    btn: {
        background: '#3b5bdb',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '13px',
        fontSize: '0.95rem',
        fontWeight: 700,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        transition: 'background 0.15s',
        marginTop: 4,
    },
    version: {
        textAlign: 'center',
        fontSize: '0.75rem',
        color: '#bbb',
        margin: '20px 0 0',
    },
};

export default Login;
